import React from "react";

/**
 * Subscribes to a media query with a server-render answer supplied by the
 * caller, so the server HTML and the first client frame agree.
 *
 * `serverSnapshot` is required on purpose: there is no correct default. The
 * server cannot know the viewport width, so the caller has to hand in whatever
 * it does know (here, the user agent).
 */
export function useMediaQuery(query: string, serverSnapshot: boolean) {
  // One MediaQueryList per query, shared by subscribe and getSnapshot.
  const mql = React.useMemo(
    () => (typeof window === "undefined" ? null : window.matchMedia(query)),
    [query],
  );

  // Must be referentially stable, or useSyncExternalStore resubscribes on
  // every render.
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (!mql) {
        return () => {};
      }

      mql.addEventListener("change", onChange);

      return () => {
        mql.removeEventListener("change", onChange);
      };
    },
    [mql],
  );

  // Returns a primitive, so useSyncExternalStore's Object.is check is stable.
  // Only ever called on the client, where mql is non-null.
  const getSnapshot = React.useCallback(() => mql!.matches, [mql]);

  return React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => serverSnapshot,
  );
}
